import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Result } from '../shared/result';
import { SearchService } from '../search.service';
import { SearchContext } from '../shared/search-context';
import { ConfigService } from '../config.service';
import { ConfigResponse, ResultCard } from '../shared/config-response';
import { ResultMapping } from '../shared/result-mapping';
import { ColumnMapping } from '../shared/column-mapping';
import { SwirlService } from '../swirl.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-results',
    templateUrl: './results.component.html',
    styleUrls: ['./results.component.scss'],
    standalone: false
})

export class ResultsComponent implements OnInit {

  result: Result;
  globalShowDetails: boolean = false;
  searchContext: SearchContext;
  resultMapping: ResultMapping;
  columnMapping: Array<ColumnMapping>;
  resultType: string;
  resultLayout: ResultCard;
  highlights: any;
  page: number;
  isLoading: boolean;
  isShowing: boolean;
  isCardView: boolean;
  config: ConfigResponse;
  isDateFilter: boolean;
  checkedSelectItems: boolean;
  selectItemsLabel: string = 'Select Items';
  isDeselected: boolean = false;
  selectedItemsCount: number = 0;
  starredItemsCount: number = 0;
  timeoutBanner: string | null = null;
  
  @Input() canExportPdf = false;
  @Output() exportPdf = new EventEmitter<void>();

  constructor(private searchService: SearchService, private configService: ConfigService, private location: Location, private swirl: SwirlService) {
    this.result = new Result();
    this.searchContext = new SearchContext();
    this.resultMapping = new ResultMapping();
    this.resultType = '';
    this.resultLayout = new ResultCard();
    this.columnMapping = [];
    this.highlights = {};
    this.page = 0;
    this.isLoading = false;
    this.isShowing = false;
    this.isCardView = false;
    this.config = new ConfigResponse();
    this.isDateFilter = false;
    this.checkedSelectItems = false;
  }

  ngOnInit() {
    this.location.subscribe((event) => {
      if (!event.url || event.url === '')
        this.searchService.changeEraseEvent(true)
    });
    this.configService.getConfig().subscribe(configResponse => {
      if (configResponse) {
        this.config = configResponse;
        if (configResponse['resultType']) {
          this.resultType = configResponse['resultType'];
        }
        if (configResponse.resultLayout) {
          this.resultLayout = configResponse.resultLayout;
        }
        if (configResponse.resultColumns) {
          this.columnMapping = configResponse.resultColumns;
        }
        if (configResponse.resultFields) {
          this.resultMapping = configResponse.resultFields;
        }
      }

    });
    // Subscribe to results
    this.searchService.currentResults.subscribe(async (result) => {
      const isSearched = await firstValueFrom(this.searchService.isSearched);

      // Ensure isShowing reflects the correct state
      this.isShowing = isSearched;
      this.result = result || { docs: [] }; // Ensure result.docs is always an array

      // Look for timeout-related messages (search-level + provider-level)
      this.timeoutBanner = null;

      const allMessages = [
        ...(this.result?.messages ?? []),
        ...(this.result?.search?.messages ?? [])
      ];

      const searchTimeoutMessages: string[] = [];
      let hasSearchTimeout = false;
      let providerStatusMeta: any | null = null;

      for (const msg of allMessages) {
        const meta = this.parseJsonFromMessage(msg);
        if (!meta || !meta.type) {
          continue;
        }

        if (meta.type === '__TIMEOUT__') {
          // SP3: search-level timeout
          const msgText = this.buildSearchTimeoutMessage(meta);
          if (msgText) {
            hasSearchTimeout = true;
            searchTimeoutMessages.push(msgText);
          }
        } else if (meta.type === '__PROVIDER_STATUS__') {
          // Keep the last provider-status message (it aggregates all providers)
          providerStatusMeta = meta;
        }
      }

      const combinedMessages: string[] = [];

      // Add any search-level timeout messages first (SP3)
      if (searchTimeoutMessages.length > 0) {
        combinedMessages.push(...searchTimeoutMessages);
      }

      // Provider-level timeout banner (SP2) is only shown when NO docs came back
      if (providerStatusMeta && (this.result?.docs?.length ?? 0) === 0) {
        const provMsg = this.buildProviderTimeoutMessage(providerStatusMeta);
        if (provMsg) {
          combinedMessages.push(provMsg);
        }
      }

      // Final banner: could be just search-level, just provider-level, or both (mixed SP2+SP3)
      this.timeoutBanner = combinedMessages.length > 0 ? combinedMessages.join('\n') : null;

      // Global flag – true ONLY for search-level timeout (SP3)
      this.searchService.setHasSearchTimeout(hasSearchTimeout);

      // Calculate starred items count for current page
      this.calculateStarredItemsCount();
    });
    this.searchService.currentSearchContext.subscribe(searchContext => {
      this.searchContext = searchContext;
      this.page = (searchContext.start / searchContext.rows) + 1;
    });
    this.searchService.currentHighlights.subscribe(highlights => {
      this.highlights = highlights;
    });
    this.searchService.eraseEvent.subscribe((isErased) => {
      if (isErased) {
        this.isLoading = true;
        this.searchService.selectedItems.next(false)
        this.checkedSelectItems = false
      }
    })
    this.searchService.isLoading.subscribe(isLoading => this.isLoading = isLoading);
    this.searchService.isCardView.subscribe(isCardView => this.isCardView = isCardView);
    this.searchService.isNewSearch.subscribe(isNewSearch => this.isShowing = false)
    this.searchService.resultMixer.subscribe(resultMixer => this.isDateFilter = resultMixer === 'DateMixer')
    this.searchService.isNewSearch.subscribe(isNewSearch => {
      this.checkedSelectItems = !isNewSearch
      this.searchService.selectedItems.next(false)
      this.searchService.clearSelectedItems()
    })
    this.swirl.getBrandingValues('config').subscribe((values) => {
      this.selectItemsLabel = values.select_items_label ?? this.selectItemsLabel
    });
    
    // Subscribe to selected items count
    this.searchService.selectedRagItems.subscribe(selectedItems => {
      const previousCount = this.selectedItemsCount;
      this.selectedItemsCount = selectedItems.length;
      
      // If items were manually added (count increased) and we're in deselected state, reset it
      if (this.selectedItemsCount > previousCount && this.isDeselected) {
        this.isDeselected = false;
      }
    });
  }

  toggleAllDetails(): void {
    this.globalShowDetails = !this.globalShowDetails; // Toggle state
  }


  toggleChange() {
    this.checkedSelectItems = !this.checkedSelectItems;
    this.searchService.cleanAllToggle.next(false);

    if (this.checkedSelectItems) {
      // Reactivating selection: ensure any prior "deselected" state is cleared
      this.isDeselected = false;
      
      // 1. Clear any existing selections first
      this.searchService.clearSelectedItems();
      
      // 2. Apply selection logic to current page results
      for (const doc of this.result.docs) {
        this.searchService.addToSelectedItemsWithLogic(doc);
      }
      
      // 3. Set a flag to indicate that selection mode is active
      this.searchService.selectedItems.next(true);
    } else {
      this.searchService.clearSelectedItems();
      this.searchService.cleanAllToggle.next(true);
      this.isDeselected = false;
      this.searchService.selectedItems.next(false);
    }
  }

  toggleSelectDeselect() {
    if (!this.isDeselected) {
      // "Deselect All" action:
      this.searchService.clearSelectedItems();
      this.searchService.cleanAllToggle.next(true);
      this.isDeselected = true;
    } else {
      // "Select All" action:
      // Apply selection logic to current page results (only items that meet criteria)
      for (const doc of this.result.docs) {
        this.searchService.addToSelectedItemsWithLogic(doc);
      }
      // Also update the state used by the "Select Items" toggle.
      this.checkedSelectItems = true;
      this.searchService.selectedItems.next(true);
      this.searchService.cleanAllToggle.next(false);
      this.isDeselected = false;
    }
  }

  hasSelectedItems(): boolean {
    return this.selectedItemsCount > 0;
  }

  /**
   * Extract JSON payload from a log/message string.
   * Many messages are prefixed with timestamps, so we
   * look for the first '{' and parse from there.
   */
  private parseJsonFromMessage(message: string): any | null {
    if (!message) {
      return null;
    }
    const jsonStart = message.indexOf('{');
    if (jsonStart === -1) {
      return null;
    }
    try {
      return JSON.parse(message.slice(jsonStart));
    } catch {
      return null;
    }
  }

  /**
   * Build a human-readable message for search-level timeouts (SP3)
   * from the __TIMEOUT__ meta emitted by the backend.
   */
  private buildSearchTimeoutMessage(meta: any): string | null {
    if (!meta || meta.type !== '__TIMEOUT__') {
      return null;
    }

    if (meta.reason !== 'SEARCH_TIMEOUT') {
      return null;
    }

    const seconds = meta.search_timeout_seconds;
    const providersDetail = meta.providers_detail || [];
    const names = providersDetail
      .map((p: any) => p?.name)
      .filter((n: string | undefined) => !!n);

    if (names.length > 0 && seconds != null) {
      const joined = names.join(', ');
      return `Results were not available in ${seconds}s from the following providers: ${joined}. Please try again in a moment.`;
    }

    if (seconds != null) {
      return `Results were not available in ${seconds}s from one or more providers. Please try again in a moment.`;
    }

    return `Results were not available from one or more providers before the search timeout. Please try again in a moment.`;
  }

  /**
   * True only if this provider actually hit an HTTP-level timeout.
   * Mirrors the backend semantics:
   *  - status === 'PROVIDER_TIMEOUT', or
   *  - retrieved/found are the -1 timeout sentinel.
   */
  private isProviderHttpTimeout(p: any): boolean {
    if (!p) return false;

    const status = (p.status ?? '').toString().toUpperCase();
    const retrieved = p.retrieved;
    const found = p.found;

    if (status === 'PROVIDER_TIMEOUT') {
      return true;
    }

    return false;
  }

  /**
   * Build a banner for provider-level (HTTP) timeouts.
   * Uses provider names from __PROVIDER_STATUS__.providers[].name,
   * but **only** for providers that actually timed out.
   */
  private buildProviderTimeoutMessage(meta: any): string | null {
    if (!meta || !Array.isArray(meta.providers) || meta.providers.length === 0) {
      return null;
    }

    // Only include providers that truly timed out at HTTP level
    const timedOutProviders = meta.providers.filter((p: any) =>
      this.isProviderHttpTimeout(p)
    );

    // If none of the providers actually timed out, do not show a banner
    if (timedOutProviders.length === 0) {
      return null;
    }

    // Build "Name (HTTP connect=Xs, read=Ys)" for each timed-out provider
    const providerParts = timedOutProviders.map((p: any) => {
      const name = p?.name || `Provider ${p?.id}`;
      const timeout = p?.timeout || {};
      const connect = timeout?.connect;
      const read = timeout?.read;

      if (typeof connect === 'number' && typeof read === 'number') {
        return `${name} (HTTP connect=${connect}s, read=${read}s)`;
      }

      // No numeric timeout info: just return the name
      return name;
    });

    const list = providerParts.join(', ');

    return `Results were not available from the following providers that timed out at the source level: ${list}.`;
  }

  calculateStarredItemsCount(): void {
    if (!this.result || !this.result.docs) {
      this.starredItemsCount = 0;
      return;
    }
    
    this.starredItemsCount = this.result.docs.filter(doc => this.searchService.shouldSelectItem(doc)).length;
  }

  onExportPdfClick(): void {
    this.exportPdf.emit();
  }
}
